import React, { useEffect, useState, useRef } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  createPlanningType,
  updatePlanningType,
  deletePlanningType,
  getPlanningType
} from '../api/planning-typeAPI';
import { fetchData } from '../utils/apiHelpers';

const PlanningTypeListLayer = () => {
  const userProfile = useUserProfile();
  const [typeList, setTypeList] = useState([]);
  const [newType, setNewType] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchPlanningTypes = () => {
    fetchData(getPlanningType, {}, setTypeList);
  };


  const validateForm = () => {
    const newErrors = {};
    if (!newType.typeDescription?.trim()) newErrors.typeDescription = 'Type Description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchPlanningTypes();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (typeList.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: {
          paginate: { first: '', last: '', next: '', previous: '' },
        },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [typeList]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewType((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (item) => {
    setNewType(item);
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset type?')) return;
    try {
      await deletePlanningType(id);
      // repaint the table
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchPlanningTypes();
    } catch (error) {
      console.error('Error deleting asset type:', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      const cleanPayload = {};
      for (const key in newType) {
        if (newType[key] !== null && typeof newType[key] !== 'object') {
          cleanPayload[key] = newType[key];
        }
      }

      if (editingId) {
        await updatePlanningType(editingId, cleanPayload);
      } else {
        await createPlanningType(cleanPayload);
      }

      setShowModal(false);
      setEditingId(null);

      // repaint the table
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }

      setNewType({});
      if (dtRef.current) dtRef.current.destroy();
      await fetchPlanningTypes();
    } catch (error) {
      console.error('Error saving asset type:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('planning-types:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewType({});
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Planning Type
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {typeList.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.typeDescription}</strong></td>
                  <td>{item.description}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('planning-types:update') && (
                      <button
                        className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                        onClick={() => handleEdit(item)}
                      >
                        <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                      </button>
                      )}
                      {userProfile?.privileges?.includes('planning-types:delete') && (
                      <button
                        className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Planning Type' : 'New Planning Type'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Name *</label>
                    <input
                      className={`form-control ${errors.typeDescription ? 'is-invalid' : ''}`}
                      name="typeDescription"
                      value={newType.typeDescription || ''}
                      onChange={handleInputChange}
                    />
                    {errors.typeDescription && (
                      <div className="invalid-feedback">{errors.typeDescription}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Description</label>
                    <input
                      className="form-control"
                      name="description"
                      value={newType.description || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanningTypeListLayer;
